import React, {createContext, useContext, useEffect, useState} from "react";
import {toast} from 'react-toastify';
import useTranslation from "../hooks/useTranslation";
import {useAuth} from "./AuthContext";
import {axiosInstance} from "../bricks/axios";
import {API} from "../config/Config";

export const AppDataContext = createContext();

export const AppDataProvider = ({children}) => {
    const [appConfig, setAppConfig] = useState();
    const [selectedPeriod, setSelectedPeriod] = useState();
    const [period, setPeriod] = useState();
    const [dataGridSettings, setDataGridSettings] = useState();
    const [allDataLoaded, setAllDataLoaded] = useState(false);
    const [sortingTestQuestionsMap, setSortingTestQuestionsMap] = useState(null);

    const {isLoggedIn} = useAuth();
    const {t} = useTranslation();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const {data} = await axiosInstance.get("api/appConfig");
                setAppConfig(data);
                setSelectedPeriod(data.actualPeriod);
                const {data: periodData} = await axiosInstance.get(`/api/period/byPeriod/${data.actualPeriod}`);
                setPeriod(periodData);
                const {data: userData} = await axiosInstance.get("/api/user");
                setDataGridSettings(userData.dataGridSettings);
            } catch (error) {
                setError(error);
            } finally {
                setAllDataLoaded(true);
            }
        };

        if (isLoggedIn) {
            fetchData();
        }
    }, [isLoggedIn]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const {data} = await axiosInstance.get(`/api/period/byPeriod/${selectedPeriod}`);
                if (period && data) {
                    setSuccess('Perioda úspěšně změněna');
                    setSortingTestQuestionsMap(null);
                    setPeriod(data);
                } else {
                    setError('Chyba. Zkuste aktualizovat okno prohlížeče');
                }
            } catch (error) {
                setError(error);
            }
        };

        if (isLoggedIn && selectedPeriod) {
            fetchData();
        }
    }, [isLoggedIn, selectedPeriod]);

    const setError = e => {
        let msg = e.message;

        if (e.response?.data?.error) {
            toast.error(
                <div>
                    <h3>{t(e.response.data.errorCode)}</h3>
                    <pre>{JSON.stringify(e.response.data.error, null, 2)}</pre>
                </div>, {
                    autoClose: 5000,
                    closeButton: true,
                    html: true
                });
            return;
        }

        if (e.response?.data) {
            if (e.response.data?.errorCode) {
                msg = t(e.response.data.errorCode);
            } else if (e.response.data?.errorMessage) {
                msg = e.response.data.errorMessage;
            }
        }

        toast.error(msg);
    }

    const setInfo = message => toast.info(message);
    const setSuccess = message => toast.success(message);

    const getDataGridColumnsSettings = (tableId) => {
        if (dataGridSettings?.[tableId]) {
            return Object.fromEntries(dataGridSettings[tableId].columns.map(column => [column.key, column]));
        }
        return null;
    }

    const setDataGridColumnsSettings = (tableId, newSettings) => {
        setDataGridSettings(prevState => {
            const newState = {...prevState};
            newState[tableId] = {...newState[tableId], columns: newSettings};
            return newState;
        })
    }

    const _loadSortingTestQuestionsList = async (period = selectedPeriod) => {
        try {
            const {method, url} = API.loadTestQuestionsByPeriodAndType(period, 'sortingTest');
            const {data} = await axiosInstance[method](url);
            return data;
        } catch (error) {
            setError(error);
        }
    }

    const handleGetSortingTestQuestionsList = async () => {
        if (sortingTestQuestionsMap) {
            return sortingTestQuestionsMap;
        } else {
            const testQuestionList = await _loadSortingTestQuestionsList();
            setSortingTestQuestionsMap(testQuestionList);
            return testQuestionList;
        }
    }

    const handlePeriodUpdate = period => {
        setPeriod(period);
    }

    return (
        <AppDataContext.Provider
            value={{
                appConfig,
                selectedPeriod,
                period,
                setSelectedPeriod,
                setError,
                setInfo,
                setSuccess,
                setAppConfig,
                setDataGridSettings,
                getDataGridColumnsSettings,
                setDataGridColumnsSettings,
                setPeriod: handlePeriodUpdate,
                getSortingTestQuestionsList: handleGetSortingTestQuestionsList,
            }}>
            {(allDataLoaded || !isLoggedIn) && children}
        </AppDataContext.Provider>
    );
};

export const useAppData = () => {
    return useContext(AppDataContext);
};
